type ProductionReportQualitySlotLike = {
  time_slot: string | null | undefined;
};

type GetApplicableProductionReportQualitySlotsParams<
  TSlot extends ProductionReportQualitySlotLike,
> = {
  slots: TSlot[];
  lineStartTime: string | null | undefined;
  lineEndTime: string | null | undefined;
  shiftStartTime: string | null | undefined;
  shiftEndTime: string | null | undefined;
};

function normalizeTime(value: string | null | undefined) {
  if (!value) return '';

  return value.slice(0, 5);
}

function timeToMinutes(value: string | null | undefined) {
  const normalized = normalizeTime(value);

  if (!normalized) return null;

  const [hour, minute] = normalized.split(':').map(Number);

  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;

  return hour * 60 + minute;
}

function getMinutesFromShiftStart(
  value: string | null | undefined,
  shiftStartTime: string | null | undefined,
) {
  const minutes = timeToMinutes(value);
  const shiftStartMinutes = timeToMinutes(shiftStartTime);

  if (minutes === null || shiftStartMinutes === null) return null;

  return minutes < shiftStartMinutes
    ? minutes + 24 * 60 - shiftStartMinutes
    : minutes - shiftStartMinutes;
}

export function getApplicableProductionReportQualitySlots<
  TSlot extends ProductionReportQualitySlotLike,
>({
  slots,
  lineStartTime,
  lineEndTime,
  shiftStartTime,
  shiftEndTime,
}: GetApplicableProductionReportQualitySlotsParams<TSlot>) {
  const normalizedLineStartTime = normalizeTime(lineStartTime);
  const normalizedLineEndTime = normalizeTime(lineEndTime);
  const effectiveLineEndTime = normalizedLineEndTime || normalizeTime(shiftEndTime);

  if (!normalizedLineStartTime || !normalizeTime(shiftStartTime)) {
    return [];
  }

  const lineStartMinutes = getMinutesFromShiftStart(
    normalizedLineStartTime,
    shiftStartTime,
  );

  const effectiveLineEndMinutes = effectiveLineEndTime
    ? getMinutesFromShiftStart(
        effectiveLineEndTime,
        shiftStartTime,
      )
    : null;

  if (lineStartMinutes === null) return [];

  return slots.filter((slot) => {
    const slotTime = normalizeTime(slot.time_slot);

    if (!slotTime) return false;

    const slotMinutes = getMinutesFromShiftStart(
      slotTime,
      shiftStartTime,
    );

    if (slotMinutes === null || slotMinutes < lineStartMinutes) {
      return false;
    }

    if (effectiveLineEndMinutes !== null) {
      const isOpenLine = !normalizedLineEndTime;

      if (isOpenLine) {
        if (slotMinutes >= effectiveLineEndMinutes) return false;
      } else if (slotMinutes > effectiveLineEndMinutes) {
        return false;
      }
    }

    return true;
  });
}